<template>
  <div :class="['container container--sm', $style.header]">
    <h1 :class="['h2', $style.title]">
      {{ t('prices.title') }}
    </h1>
    <div :class="$style.caption">
      {{ t('prices.subtitle') }}
    </div>
  </div>

  <div :class="$style.categoryWrapper">
    <UiTimeLine
      :lines="categoryTabs"
      v-model="activeCategory"
    />
  </div>

  <div
    v-if="activeCategory === 'mobile'"
    :class="['container container--sm', $style.categoryCaption]"
  >
    {{ t('prices.tabs.mobileDescription') }}
  </div>

  <div :class="$style.timelineWrapper">
    <UiTimeLine
      :lines="timeLine"
      v-model="activeTimeLine"
    />
  </div>

  <SubscrbPlanContainer>
    <template #list>
      <SubscrbPlanList
        v-model="activePlanId"
        :loading="loader"
        :user="user ? true : false"
        @action="handlePlanAction"
      />
    </template>

    <template #details>
      <transition-group name="fade">
        <SubscrbPlanParamsSkeleton
          v-show="loader"
        />
      </transition-group>
      <transition-group name="fade">
        <client-only>
          <SubscrbPlanParams
            v-show="!loader"
          >
            <template #actionButton>
              <UiButton
                color="gray"
                wide
                @click.prevent="openSubscbRegModal"
              >
                <template #label>
                 {{ activePlan?.name === 'Custom' ? t('prices.buttons.customPlan') : t('prices.buttons.plan') }}
                </template>

                <template #preppendIcon>
                  <UiIconsArrowTopRight />
                </template>
              </UiButton>
            </template>
          </SubscrbPlanParams>
        </client-only>
      </transition-group>
    </template>
  </SubscrbPlanContainer>

  <SubscrbCustomPlan v-if="activeCategory === 'tariffs'" />
  <SubscrbPriceTable
    :user="user ? true : false"
    @action="handlePlanAction"
  />
  <PricesPromo />
  <PricesPaymentTypes />
</template>

<script setup lang="ts">
import type { TimeLineItem } from '@/components/ui/TimeLine/index.js';
import type { PlanItemDto } from '~/dto';
import { eventTracker, AnalyticsEventKey } from '~/utils/analyticServices';

definePageMeta({
  ssr: false,
});

const CLOUD_DEVICE_FILTER = '[[{"key":"type","operand":"$eq","value":"cloudDevice"}]]';

const { t } = useI18n();

useHead({
  title: t('meta.prices.title'),
});

const {
  user,
} = storeToRefs(useAuthStore());
const {
  subscrb,
} = storeToRefs(useSubsrbStore());

const { $mixpanel } = useNuxtApp();

const toastStore = useToastStore();
const loader = ref<boolean>(true);
const activeCategory = ref<'tariffs' | 'mobile'>('tariffs');
const activeTimeLine = ref<'1' | '3' | '6' | '12'>('12');
const activePlanId = ref<string>('');
const tariffsPlans = ref<PlanItemDto[]>([]);
const mobilePlans = ref<PlanItemDto[]>([]);

const listOfPlans = computed<PlanItemDto[]>(() => (
  activeCategory.value === 'tariffs' ? tariffsPlans.value : mobilePlans.value
));
const activePlan = computed<PlanItemDto | undefined>(() => (
  listOfPlans.value.find((plan) => plan._id === activePlanId.value)
));

const categoryTabs = computed<TimeLineItem[]>(() => [
  {
    label: t('prices.tabs.tariffs'),
    value: 'tariffs',
  },
  {
    label: t('prices.tabs.mobileVirtualization'),
    value: 'mobile',
  },
]);

const timeLine = computed<TimeLineItem[]>(() => [
  {
    label: t('prices.timeline.1'),
    value: '1',
  }, {
    label: t('prices.timeline.3'),
    value: '3',
  }, {
    label: t('prices.timeline.6'),
    value: '6',
  }, {
    label: t('prices.timeline.12'),
    value: '12',
  },
]);

const normalizePlans = (data: PlanItemDto[]): PlanItemDto[] => data
  .map(plan => {
    const filteredPlan = { ...plan };
    if (filteredPlan.features && filteredPlan.features.hasOwnProperty('mobileAppAccess')) {
      delete filteredPlan.features.mobileAppAccess;
    }
    return filteredPlan;
  })
  .sort((prev, next) => Number(prev.price.amount) - Number(next.price.amount));

const fetchPlans = async (): Promise<void> => {
  try {
    loader.value = true;
    const paymentApi = useNuxtApp().$paymentApi;

    const [tariffsResponse, mobileResponse] = await Promise.all([
      paymentApi.getListOfPlansApi(),
      paymentApi.getListOfPlansApi(CLOUD_DEVICE_FILTER),
    ]);

    if (!tariffsResponse.result || !mobileResponse.result) {
      throw new Error('Error @getListOfPlansApi');
    }

    tariffsPlans.value = normalizePlans(tariffsResponse.data)
      .filter(plan => plan.type !== 'cloudDevice');
    mobilePlans.value = normalizePlans(mobileResponse.data);
    loader.value = false;
  } catch (error) {
    console.error(error);
    toastStore.showDanger({
      text: 'Couldn’t get a list of plans',
      duration: 3000,
    });

    tariffsPlans.value = [];
    mobilePlans.value = [];
  } finally {
    loader.value = false;
  }
};

const chooseDefaultActivePlan = (list: PlanItemDto[]) => {
  if (!list || !list.length) {
    activePlanId.value = '';
    return;
  }

  const currentExists = list.some(plan => plan._id === activePlanId.value);
  if (currentExists) return;

  const starterPlan = list.find((plan: PlanItemDto) => plan.name === 'Starter' || plan.name === 'Starter-1');

  if (starterPlan) {
    activePlanId.value = starterPlan._id;
  } else {
    activePlanId.value = list[0]._id;
  }
};

const { $localePath } = useNuxtApp();
const modalsStore = useModalStore();
const openPromocodeModal = () => {
  if (!user.value) return navigateTo($localePath('/auth/signup'));

  modalsStore.showModal({
    type: modalType.PROMOCODE_MODAL,
  });
};

const openSubscbRegModal = () => {
  if (!user.value) return navigateTo($localePath('/auth/signup'));

  if (activePlan.value?.name === 'Custom') {
    return window.open('https://t.me/zerodetect_support', '_blank');
  }

  if (activePlan.value?.name === 'Free') {
    $mixpanel.track('free_trial', {
      plan_type: activePlan.value.name
    });
  }

  modalsStore.showModal({
    type: modalType.REG_SUBSCRB,
    payload: {
      userEmail: user.value?.email,
      userId: user.value._id,
      plan: activePlan.value,
      timeline: activeTimeLine.value,
      activePaymentSystem: subscrb.value?.paymentSystem || null,
    },
  });
};

const handlePlanAction = (planId: string, fromTable?: boolean) => {
  activePlanId.value = planId;
  if (fromTable) {
    activeTimeLine.value = '1';
  }
  openSubscbRegModal();
};

provide('plans', listOfPlans);
provide<typeof activeTimeLine>('activeTimeLine', activeTimeLine);
provide<typeof activePlanId>('activePlanId', activePlanId);
provide<typeof activePlan>('activePlan', activePlan);

watch(activeCategory, () => {
  chooseDefaultActivePlan(listOfPlans.value);
});

watch(listOfPlans, (list) => {
  chooseDefaultActivePlan(list);
});

onMounted(async () => {
  await fetchPlans();

  eventTracker(AnalyticsEventKey.PricingPageViewed, {
    platform: 'WebSite',
  });

  chooseDefaultActivePlan(listOfPlans.value);
});
</script>

<style lang="scss" module>
  .header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding-top: 32px;
    padding-bottom: 30px;
    text-align: center;

    .special {
      display: block;
      color: red;
      font-size: 32px;

      @media (max-width: 1023px) {
        font-size: 22px;
      }

      @media (max-width: 375px) {
        font-size: 19px;
      }
    }
  }

  .caption {
    max-width: 448px;
  }

  .categoryWrapper {
    @media (max-width: 767px) {
      overflow-x: auto;
      margin-bottom: 24px;
      padding: 0 30px 6px 30px;
    }
  }

  .categoryCaption {
    max-width: 640px;
    margin: 40px auto;
    text-align: center;
    color: #596269;
    font-size: 18px;
    line-height: 1.45;
    white-space: pre-line;

    @media (max-width: 767px) {
      margin: 0 auto 28px;
      padding: 0 30px;
      font-size: 14px;
    }
  }

  .timelineWrapper {
    @media (max-width: 767px) {
      overflow-x: auto;
      margin-bottom: 40px;
      padding: 0 30px 6px 30px;
    }
  }
</style>
